// src/components/scenes/GridScene.js
// Filepath: // src/components/scenes/GridScene.js

import React, { useMemo, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AssetGrid from "../shared/AssetGrid";
import { useScene } from "../../state/SceneContext";
import { setSceneHandlers, focusRowCol } from "../../input/remote";
import Hero from "../common/Hero";
import Focusable from "../common/Focusable";
import gridData from "../../data/mockGrid.json";
import { useHomeDataStore } from "../../state/homeDataStore";
import {
  buildPurchasesRail,
  railsDebugEnabled,
  buildCollectionsRail,
  selectCollectionItems,
} from "../../lib/contentNormalizer";

const ANCHOR_EXTRA_OFFSET = 30;

/**
 * @typedef {"stop" | "wrapRow" | "serpentine"} HorizontalEdgePolicy
 */

/** ─────────────────────── Helpers to mirror HomeScene ───────────────────── */

// Defensive hero field pickers
const pickTitle = (item) => item?.title || item?.name || "Untitled";
const pickSubtitle = (item) =>
  item?.subtitle ?? item?.description ?? item?.summary ?? item?._raw?.artDescription ?? "";

const pickBackground = (item) =>
  item?.heroImage ||
  item?.backdrop ||
  item?.backgroundUrl ||
  item?.thumbnailUrl ||
  item?.thumbnail ||
  null;

function pickArtistFirst(item) {
  return item?.artistFirstName ?? item?._raw?.artistFirstName ?? item?.artistFirst ?? null;
}
function pickArtistLast(item) {
  return item?.artistLastName ?? item?._raw?.artistLastName ?? item?.artistLast ?? null;
}
function pickYear(item) {
  return item?.year ?? item?.artYear ?? item?._raw?.year ?? item?._raw?.artYear ?? null;
}
function pickMedium(item) {
  return item?.medium ?? item?._raw?.medium ?? null;
}
function pickMediaType(item) {
  return item?.expectedMediaType ?? item?.type ?? item?._raw?.expectedMediaType ?? null; // "Video"|"Image"|null
}
function pickAllowWallpaper(item) {
  return item?.allowWallpaper ?? item?._raw?.allowWallpaper ?? null;
}
function pickShares(item) {
  return item?.sharesYouOwn ?? item?._raw?.sharesYouOwn ?? 0;
}
function pickValue(item) {
  return item?.valueYouOwn ?? item?._raw?.valueYouOwn ?? null;
}

/** Build the enriched payload for ItemDetailScene (parity with HomeScene) */
function normalizeForDetail(item) {
  const expectedMediaType = pickMediaType(item);
  const isVideo = expectedMediaType === "Video";

  const af = pickArtistFirst(item);
  const al = pickArtistLast(item);
  const yr = pickYear(item);
  const byline =
    af || al || yr
      ? [[af, al].filter(Boolean).join(" "), yr].filter(Boolean).join(" • ")
      : null;

  return {
    id: item?.id,
    title: pickTitle(item),
    subtitle: pickSubtitle(item) || null,
    expectedMediaType,
    isVideo,
    mediaUrl: isVideo ? item?.mediaUrl ?? item?.videoUrl ?? null : null,
    previewUrl: isVideo ? item?.previewUrl ?? null : null,
    imageUrl: !isVideo ? item?.imageUrl ?? null : null,
    durationSec: item?.durationSec ?? null,
    backgroundUrl: pickBackground(item),
    thumbnailUrl: item?.thumbnailUrl ?? item?.thumbnail ?? null,

    // hero metadata
    byline,                                   // "Artist • Year"
    medium: pickMedium(item) || null,
    allowWallpaper: !!pickAllowWallpaper(item),
    sharesYouOwn: Number(pickShares(item) || 0),
    valueYouOwn: pickValue(item),

    _raw: item,
  };
}

function isDebugItem() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("debugItem") === "1") return true;
      if (window.__KBEAN_DEBUG_ITEM === true) return true;
    }
  } catch {}
  return String(process.env.REACT_APP_DEBUG_ITEM || "").trim().toLowerCase() === "true";
}

/** ──────────────────────────────────────────────────────────────────────── */

export default function GridScene({
  source,
  title,
  query,
  collectionId,
  backTo = "home",
  loader,          // optional external loader overrides JSON/live
  cols = 5,
  sceneKey,
  /** @type {HorizontalEdgePolicy} */
  horizontalEdge = "stop",
}) {
  const { setScene, pushScene } = useScene();

  // Resolve title
  const resolvedTitle = useMemo(() => title || titleFor(source, query), [title, source, query]);

  // Resolve items from JSON (unless a loader prop is provided).
  // IMPORTANT: We intentionally do NOT pull 'collections' nor 'collectionItems' from JSON anymore.
  const itemsForSource = useMemo(() => {
    if (loader) return null; // caller will load
    switch (source) {
      case "watchlist": return gridData.watchlist || [];
      case "published": return gridData.published || [];
      // "collections" and "collectionItems" now use live loaders below
      case "purchases": return []; // purchases use live loader
      case "activity": return [];  // activity use live loader
      default:
        return [];
    }
  }, [source, loader]);

  // JSON wrapper for non-live sources
  const jsonLoader = React.useCallback(async () => itemsForSource, [itemsForSource]);

  // Live purchases loader (unchanged)
  const purchasesLoader = React.useCallback(async () => {
    const store = useHomeDataStore.getState();
    if (!store.holdings?.data) {
      try {
        await store.refresh({ force: true });
      } catch {}
    }

    const latest = useHomeDataStore.getState();
    const rail = buildPurchasesRail(latest.holdings?.data);
    const items = rail?.items || [];

    if (railsDebugEnabled()) {
      try {
        console.groupCollapsed(`[grid:purchases] ${items.length} item(s)`);
        console.table(
          items.map((i, idx) => ({
            idx,
            id: i.id,
            title: i.title,
            type: i.expectedMediaType,
            mediaUrl: i.mediaUrl,
            thumb: i.thumbnailUrl,
          }))
        );
        console.groupEnd();
      } catch {}
    }

    return items;
  }, []);

  // Live collections rail loader
  const collectionsLoader = React.useCallback(async () => {
    const store = useHomeDataStore.getState();
    if (!store.holdings?.data || !store.profile?.data) {
      try {
        await store.refresh({ force: true });
      } catch {}
    }
    const latest = useHomeDataStore.getState();

    // contentNormalizer expects (userProfile, myHoldingsResponse)
    const rail = buildCollectionsRail(latest.profile?.data, latest.holdings?.data);
    const items = rail?.items || [];

    if (railsDebugEnabled()) {
      try {
        console.groupCollapsed(`[grid:collections] ${items.length} collection(s)`);
        console.table(
          items.map((i, idx) => ({
            idx,
            id: i.id,
            title: i.title,
            ownedCount: i._ownedArtIds?.length || 0,
            cover: i.thumbnailUrl,
          }))
        );
        console.groupEnd();
      } catch {}
    }

    return items;
  }, []);

  // Live collection items loader
  const collectionItemsLoader = React.useCallback(async () => {
    const store = useHomeDataStore.getState();
    if (!store.holdings?.data || !store.profile?.data) {
      try {
        await store.refresh({ force: true });
      } catch {}
    }
    const latest = useHomeDataStore.getState();
    const items = selectCollectionItems(
      collectionId,
      latest.profile?.data,
      latest.holdings?.data
    );

    if (railsDebugEnabled()) {
      try {
        console.groupCollapsed(`[grid:collectionItems:${collectionId}] ${items.length} item(s)`);
        console.table(
          items.map((i, idx) => ({
            idx,
            id: i.id,
            title: i.title,
            type: i.expectedMediaType,
            mediaUrl: i.mediaUrl,
            thumb: i.thumbnailUrl,
          }))
        );
        console.groupEnd();
      } catch {}
    }

    return items;
  }, [collectionId]);

  // Live activity loader (uses Zustand normalization)
  const activityLoader = React.useCallback(async () => {
    const store = useHomeDataStore.getState();
    if (!store.activity?.data) {
      try {
        await store.refresh({ force: true });
      } catch {}
    }

    const latest = useHomeDataStore.getState();
    const items = latest.selectActivityItems?.() || [];

    if (railsDebugEnabled()) {
      try {
        console.groupCollapsed(`[grid:activity] ${items.length} item(s)`);
        console.table(
          items.map((i, idx) => ({
            idx,
            id: i.id,
            title: i.title,
            type: i.expectedMediaType,
            mediaUrl: i.mediaUrl,
            thumb: i.thumbnailUrl,
          }))
        );
        console.groupEnd();
      } catch {}
    }

    return items;
  }, []);

  // Choose effective loader
  const effectiveLoader = loader
    ? loader
    : source === "purchases"
    ? purchasesLoader
    : source === "activity"
    ? activityLoader
    : source === "collections"
    ? collectionsLoader
    : source === "collectionItems"
    ? collectionItemsLoader
    : jsonLoader;

  const [activeItem, setActiveItem] = useState(null);
  const [gridItems, setGridItems] = useState([]);

  const heroTitle = activeItem?.title || resolvedTitle;
  const heroBg =
    activeItem?.backgroundUrl ||
    activeItem?.backdrop ||
    activeItem?.thumbnailUrl ||
    null;

  // --- Grid-scope DOM helpers, memoized for ESLint + stable refs ---
  const scopeEl = useCallback(
    () =>
      document.querySelector('[data-scope="rails"]') ||
      document.querySelector('[data-rails-root]') ||
      document,
    []
  );

  const getActiveFocusable = useCallback(() => {
    const rails = scopeEl();
    const tagged = rails.querySelector?.('[data-focusable="true"].focused');
    if (tagged) return tagged;

    const ae = document.activeElement;
    const nearest = ae?.closest?.('[data-focusable="true"]');
    if (nearest && rails.contains(nearest)) return nearest;

    const first = rails.querySelector?.('[data-focusable="true"]');
    return first || null;
  }, [scopeEl]);

  const parseRow = useCallback((el) => {
    const v = Number(el?.dataset?.row);
    return Number.isFinite(v) ? v : null;
  }, []);

  const parseCol = useCallback((el) => {
    const v = Number(el?.dataset?.col);
    return Number.isFinite(v) ? v : null;
  }, []);

  const rowInfo = useCallback(
    (rowIndex) => {
      const rails = scopeEl();
      const nodes = Array.from(
        rails.querySelectorAll?.(`[data-focusable="true"][data-row="${rowIndex}"]`) || []
      );
      let maxCol = -1;
      nodes.forEach((n) => {
        const c = parseCol(n);
        if (Number.isFinite(c)) maxCol = Math.max(maxCol, c);
      });
      const count = maxCol >= 0 ? maxCol + 1 : 0;
      return { count, lastCol: maxCol };
    },
    [scopeEl, parseCol]
  );

  // All focusable row indices in the grid (>= 0), sorted asc
  const rowsSorted = useCallback(() => {
    const rails = scopeEl();
    const set = new Set(
      Array.from(
        rails.querySelectorAll?.('[data-focusable="true"][data-row]') || []
      )
        .map((n) => Number(n?.dataset?.row))
        .filter((n) => Number.isFinite(n) && n >= 0)
    );
    return Array.from(set).sort((a, b) => a - b);
  }, [scopeEl]);

  // Find the closest available column number in a given target row to `wantCol`
  const closestColInRow = useCallback(
    (rowIndex, wantCol) => {
      const rails = scopeEl();
      const cols = Array.from(
        rails.querySelectorAll?.(
          `[data-focusable="true"][data-row="${rowIndex}"]`
        ) || []
      )
        .map((n) => Number(n?.dataset?.col))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b);

      if (cols.length === 0) return null;
      if (cols.includes(wantCol)) return wantCol;

      return cols.reduce((best, c) => {
        const d = Math.abs(c - wantCol);
        const bd = Math.abs((best ?? cols[0]) - wantCol);
        return d < bd ? c : best;
      }, null);
    },
    [scopeEl]
  );

  // DPAD handlers (grid-specific)
  React.useEffect(() => {
    setSceneHandlers({
      onBack: () => {
        const target =
          typeof backTo === "string"
            ? { name: backTo, params: {} }
            : backTo && typeof backTo === "object" && backTo.name
              ? { name: backTo.name, params: backTo.params || {} }
              : { name: "home", params: {} };

        const focusKey = source === "collectionItems" ? "collections" : source;
        const goingHome = target.name === "home";

        setScene({
          name: target.name,
          params: {
            ...target.params,
            ...(goingHome ? { openSideNav: true, focusNavKey: focusKey } : {}),
          },
        });

        return true;
      },

      // Up in Grid: prefer same column in the previous data row
      onUp: () => {
        const el = getActiveFocusable();
        if (!el) return true;

        const curRow = parseRow(el);
        const curCol = parseCol(el);

        // FIX: Only try to hop to slideshow when it's actually rendered
        if (source !== "collections" && curRow === 0) {
          const handled = focusRowCol(-1, 0, { retryMs: 150 });
          if (handled) return true;
        }

        // Normal grid Up behavior: same column index, nearest fallback
        const rows = rowsSorted();
        if (!rows.length || curRow == null || curCol == null) return true;

        // Find previous row < curRow
        let prevRow = null;
        for (let i = rows.length - 1; i >= 0; i--) {
          if (rows[i] < curRow) { prevRow = rows[i]; break; }
        }
        if (prevRow == null) return true; // edge-stop

        const targetCol = closestColInRow(prevRow, curCol);
        if (targetCol == null) return true;

        focusRowCol(prevRow, targetCol, { retryMs: 150 });
        return true;
      },

      // Right edge policy remains as before (grid-only)
      onRight: () => {
        const el = getActiveFocusable();
        if (!el) return false;

        if (el.closest?.('[data-scope="sidenav"]')) return false;

        const r = parseRow(el);
        const c = parseCol(el);
        if (r == null || c == null) return false;

        const { count, lastCol } = rowInfo(r);
        if (count === 0) return false;

        if (c < lastCol) return false; // let default Right move

        if (horizontalEdge === "wrapRow") {
          return focusRowCol(r, 0, { retryMs: 150 });
        }
        if (horizontalEdge === "serpentine") {
          const nextRow = r + 1;
          const nextInfo = rowInfo(nextRow);
          if (nextInfo.count > 0) {
            return focusRowCol(nextRow, 0, { retryMs: 150 });
          }
          return true; // stop at last row
        }
        return true; // "stop"
      },

      // Left edge policy remains as before (do not override side nav entry)
      onLeft: () => {
        const el = getActiveFocusable();
        if (!el) return false;

        if (el.closest?.('[data-scope="sidenav"]')) return false;

        const r = parseRow(el);
        const c = parseCol(el);
        if (r == null || c == null) return false;

        if (c === 0) return false; // allow Left to reach side nav
        if (c > 0) return false;   // let default Left move
        return true;               // safety stop
      },

      // Down in Grid: prefer same column in the next data row
      onDown: () => {
        const el = getActiveFocusable();
        if (!el) return true;

        const curRow = parseRow(el);
        const curCol = parseCol(el);

        const rows = rowsSorted();
        if (!rows.length || curRow == null || curCol == null) return true;

        // Find next row > curRow
        const nextRow = rows.find((r) => r > curRow);
        if (nextRow == null) return true; // edge-stop

        const targetCol = closestColInRow(nextRow, curCol);
        if (targetCol == null) return true;

        focusRowCol(nextRow, targetCol, { retryMs: 150 });
        return true;
      },

      onEnter: undefined,
    });

    return () => setSceneHandlers({});
  }, [
    backTo,
    setScene,
    source,
    horizontalEdge,
    getActiveFocusable,
    parseRow,
    parseCol,
    rowInfo,
    rowsSorted,
    closestColInRow,
  ]);

  const snapshotThisGrid = React.useCallback(
    () => ({
      name: "grid",
      params: { source, title: resolvedTitle, query, collectionId, backTo, sceneKey, horizontalEdge }
    }),
    [source, resolvedTitle, query, collectionId, backTo, sceneKey, horizontalEdge]
  );

  const handleEnter = (item) => {
    if (source === "collections" && item?.type === "collection") {
      setScene({
        name: "grid",
        params: {
          source: "collectionItems",
          title: item?.title || "Collection",
          collectionId: item?.id,
          backTo: snapshotThisGrid(),
          sceneKey: `grid-collection-${item?.id}`,
          horizontalEdge,
        },
      });
      return;
    }

    // ── NEW: Enrich the payload so ItemDetailScene has full hero metadata ──
    const payload = normalizeForDetail(item);

    if (isDebugItem()) {
      console.groupCollapsed("[GridScene] handleEnter → payload to ItemDetailScene");
      try {
        console.log("keys:", Object.keys(payload || {}));
        console.log(payload);
        if (typeof window !== "undefined") {
          window.__KBEAN_LAST_ITEM_PAYLOAD = JSON.parse(JSON.stringify(payload));
        }
      } finally {
        console.groupEnd();
      }
    }

    const backToGrid = snapshotThisGrid();
    (pushScene || setScene)({
      name: "item",
      params: { id: payload.id, item: payload, backTo: backToGrid },
    });
  };

  const startGallery = () => {
    const originals = Array.isArray(gridItems) ? gridItems : [];
    const focusId = activeItem?.id;
    const startIndex = focusId
      ? Math.max(0, originals.findIndex((i) => i?.id === focusId))
      : 0;

    (pushScene || setScene)({
      name: "gallery",
      params: {
        items: originals,
        startIndex,
        backTo: snapshotThisGrid(),
      },
    });
  };

  return (
    <Box
      key={sceneKey || `${source}-${query || ""}-${collectionId || ""}`}
      sx={{ position: "relative", width: "100%", height: "100vh", overflow: "hidden" }}
      data-scope="rails"
      data-rails-root
    >
      <Hero
        title={heroTitle}
        subtitle={null}
        backgroundUrl={heroBg}
        gradientStops="linear-gradient(135deg, rgba(0,0,0,0.90) 10%, rgba(0,0,0,0.68) 40%, rgba(0,0,0,0.28) 72%, rgba(0,0,0,0.08) 100%)"
        textTop="14vh"
        textLeft={40}
      />

      {/* Scene header */}
      <Box
        sx={{
          position: "fixed",
          top: 80,
          left: 40,
          zIndex: 950,
          px: 1.25,
          py: 0.5,
          borderRadius: "10px",
          bgcolor: "rgba(0,0,0,0.55)",
          backdropFilter: "saturate(120%) blur(2px)",
        }}
      >
        <Typography variant="h1" sx={{ color: "rgba(255,255,255,0.9)", letterSpacing: 1.2 }}>
          {resolvedTitle}
        </Typography>
      </Box>

      {source !== "collections" && (
        <Box sx={{ position: "fixed", top: 80, right: 40, zIndex: 951 }}>
          <Focusable row={-1} col={0}>
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={startGallery} data-click="true">
              Start Slideshow
            </Button>
          </Focusable>
        </Box>
      )}

      <AssetGrid
        source={source}
        query={query}
        collectionId={collectionId}
        loader={effectiveLoader}
        cols={cols}
        onEnter={handleEnter}
        onFocusItem={setActiveItem}
        onItemsLoaded={setGridItems}
        anchorOffsetPx={ANCHOR_EXTRA_OFFSET}
        gapPx={16}
        cardWidthPx={360}
      />
    </Box>
  );
}

function titleFor(source, query) {
  switch (source) {
    case "purchases": return "Your Purchases";
    case "activity": return "My Activity";
    case "watchlist": return "Watchlist";
    case "published": return "Your Studio";
    case "collections": return "Your Collections";
    case "collectionItems": return "Collection";
    case "search": return query ? `Search: ${query}` : "Search";
    default: return "Items";
  }
}
